/**
 * WhatyLive 协议定义（TypeScript 侧）
 *
 * 与 server/pkg/protocol 的 Go 定义一一对应，见 docs/03-protocol.md。
 * 位常量的值与老系统 Base/stdbase.h 逐位对齐。
 *
 * 本文件由 Web 端与 Tauri 桌面端共用。
 */

// ---------- 权限位（对应老系统 ENUM_PERMISSION）----------

export const Perm = {
  Read: 1 << 0, // r 接收文本
  Write: 1 << 1, // w 发送文本
  Speak: 1 << 2, // s 申请麦克风
  Listen: 1 << 3, // l 接收语音
  PrivateMsg: 1 << 4, // p 私聊
  Whiteboard: 1 << 5, // b 使用白板
  Video: 1 << 6, // v 发送视频
  HTML: 1 << 7, // h HTML 共享
  KickUser: 1 << 8, // K 踢人
  SetRoomProp: 1 << 9, // P 设置房间属性
  SetUserProp: 1 << 10, // U 修改他人权限
  SetOp: 1 << 11, // S 设置主持人
  Op: 1 << 12, // O 是主持人
  SetCodec: 1 << 13, // C 设置编解码
  BanIP: 1 << 14, // B 封 IP
  SetMic: 1 << 15, // M 给麦/放麦/锁麦
  SetMicNum: 1 << 16, // N 设定房间麦数
  LockRoom: 1 << 17, // L 锁定房间
  SetAdmin: 1 << 18, // A 设置临时管理员
  LockWB: 1 << 19, // 锁定白板
  OpWB: 1 << 20, // 操作白板
  AppShare: 1 << 21, // 应用共享
  Admin: 1 << 22, // 管理员
  Hide: 1 << 23, // 隐身
  SeeHide: 1 << 24, // 可见隐身用户
  DelWB: 1 << 25, // 删除白板内容
  Broadcast: 1 << 26, // 广播
  Super: 1 << 27, // 超级权限
  SendFile: 1 << 28, // f 传送文件
  Record: 1 << 29, // R 录制
} as const

/** 权限位与老系统字母表示的映射。权限在线上以字母串传输。 */
const PERM_LETTERS: ReadonlyArray<readonly [number, string]> = [
  [Perm.Read, 'r'], [Perm.Write, 'w'], [Perm.Speak, 's'], [Perm.Listen, 'l'],
  [Perm.PrivateMsg, 'p'], [Perm.Whiteboard, 'b'], [Perm.Video, 'v'], [Perm.HTML, 'h'],
  [Perm.KickUser, 'K'], [Perm.SetRoomProp, 'P'], [Perm.SetUserProp, 'U'],
  [Perm.SetOp, 'S'], [Perm.Op, 'O'], [Perm.SetCodec, 'C'], [Perm.BanIP, 'B'],
  [Perm.SetMic, 'M'], [Perm.SetMicNum, 'N'], [Perm.LockRoom, 'L'],
  [Perm.SetAdmin, 'A'], [Perm.SendFile, 'f'], [Perm.Record, 'R'],
]

/** 把字母串解析为权限位。 */
export function parsePerm(s: string): number {
  let p = 0
  for (const ch of s) {
    for (const [bit, letter] of PERM_LETTERS) {
      if (letter === ch) {
        p |= bit
        break
      }
    }
  }
  // JS 位运算结果是有符号 32 位，Perm.Record(1<<29) 之内都安全；
  // 用无符号右移 0 位归一化，避免出现负数。
  return p >>> 0
}

/** 把权限位序列化为字母串。 */
export function formatPerm(p: number): string {
  let out = ''
  for (const [bit, letter] of PERM_LETTERS) {
    if (p & bit) out += letter
  }
  return out
}

/** 判断是否具备全部给定权限位。 */
export function hasPerm(p: number, bits: number): boolean {
  return (p & bits) === bits
}

/** 判断是否具备任意一位。 */
export function hasAnyPerm(p: number, bits: number): boolean {
  return (p & bits) !== 0
}

// ---------- 用户状态位（对应老系统 ENUM_USTSTATE）----------

export const UState = {
  Speaking: 1 << 0, // 正在持麦发言
  InPrivateMsg: 1 << 1, // 私聊中
  AudioOn: 1 << 5, // 打开语音
  WhiteboardOn: 1 << 6, // 打开白板
  VideoOn: 1 << 7, // 打开视频
  TextOn: 1 << 8, // 打开文本
  Leaving: 1 << 9, // 暂离
  Hidden: 1 << 10, // 隐身
  VideoSendClosed: 1 << 12, // 不发视频
  AppShareClosed: 1 << 13, // 不收应用共享
  HTMLClosed: 1 << 14, // 不收 HTML
  Recording: 1 << 15, // 录制中
  NetLeave: 1 << 16, // 断线（录制期间保留席位）
  /** 举手。与麦位解耦：自由模式下只是示意，主持人模式下等价于申请发言 */
  HandRaised: 1 << 2,
} as const

const STATE_LETTERS: ReadonlyArray<readonly [number, string]> = [
  [UState.Speaking, 's'], [UState.InPrivateMsg, 'p'], [UState.AudioOn, 'a'],
  [UState.WhiteboardOn, 'w'], [UState.VideoOn, 'v'], [UState.TextOn, 't'],
  [UState.Leaving, 'L'], [UState.Hidden, 'H'], [UState.Recording, 'R'],
]

/** 把字母串解析为用户状态位。 */
export function parseState(s: string): number {
  let v = 0
  for (const ch of s) {
    for (const [bit, letter] of STATE_LETTERS) {
      if (letter === ch) {
        v |= bit
        break
      }
    }
  }
  return v >>> 0
}

/** 把用户状态位序列化为字母串。 */
export function formatState(v: number): string {
  let out = ''
  for (const [bit, letter] of STATE_LETTERS) {
    if (v & bit) out += letter
  }
  return out
}

// ---------- 房间标志（对应老系统 ENUM_ROOMSTATE）----------

export const RoomFlag = {
  // 属性位：房间被允许做什么
  PermText: 1 << 0,
  PermAudio: 1 << 1,
  PermVideo: 1 << 2,
  PermWhiteboard: 1 << 3,
  PermAppShare: 1 << 4,
  PermRecord: 1 << 6,
  PermSendFile: 1 << 7,
  PermHTMLShare: 1 << 8,
  // 状态位：房间当前处于什么状态
  StateRecording: 1 << 15,
  StateHTMLShare: 1 << 16,
  StateOpMode: 1 << 17, // 主持人模式
  StateTemporary: 1 << 18,
  StateAppShare: 1 << 19,
  StateVideo: 1 << 20,
  StateWhiteboard: 1 << 21,
  StateText: 1 << 22,
  StateAudio: 1 << 23,
  StateVideoToSpeakOnly: 1 << 24,
  /** 成员入会时静音：新进来的人默认不开麦（主持人不受影响）。对应腾讯会议同名选项。 */
  StateMuteOnJoin: 1 << 25,
  StateLectureMode: 1 << 28, // 讲课模式
  StateCoursewareMode: 1 << 29, // 课件模式
  StateLocked: 1 << 30,
  /**
   * 等候室。**必须写成无符号字面量**，不能写 `1 << 31`：
   * JS 位运算是 32 位有符号的，`1 << 31` 等于 -2147483648，
   * 发给服务端的 uint32 字段会 unmarshal 失败 —— 开关静默失效，界面上什么都不会提示。
   */
  StateWaitingRoom: 2147483648,
  /** 自由发言：人人可自行开麦，不受麦位数限制（对齐腾讯会议） */
  StateFreeSpeak: 1 << 13,
} as const

const ROOM_LETTERS: ReadonlyArray<readonly [number, string]> = [
  [RoomFlag.StateText, 't'], [RoomFlag.StateAudio, 'a'], [RoomFlag.StateVideo, 'v'],
  [RoomFlag.StateWhiteboard, 'w'], [RoomFlag.StateOpMode, 'o'], [RoomFlag.StateAppShare, 'p'],
  [RoomFlag.StateRecording, 'R'], [RoomFlag.StateHTMLShare, 'h'],
  [RoomFlag.StateLectureMode, 'l'], [RoomFlag.StateCoursewareMode, 'c'],
  [RoomFlag.StateLocked, 'L'], [RoomFlag.StateTemporary, 'T'],
  [RoomFlag.StateWaitingRoom, 'W'],
  [RoomFlag.StateVideoToSpeakOnly, 'S'],
]

/** 把字母串解析为房间标志位。 */
export function parseRoomFlags(s: string): number {
  let v = 0
  for (const ch of s) {
    for (const [bit, letter] of ROOM_LETTERS) {
      if (letter === ch) {
        v |= bit
        break
      }
    }
  }
  return v >>> 0
}

/** 把房间标志位序列化为字母串。 */
export function formatRoomFlags(v: number): string {
  let out = ''
  for (const [bit, letter] of ROOM_LETTERS) {
    if (v & bit) out += letter
  }
  return out
}

// ---------- 用户关系（对应老系统 ENUM_RELATION）----------

export const Rel = {
  NoReceiveAudio: 1 << 0, // 屏蔽此人语音
  NoReceiveText: 1 << 1, // 屏蔽此人文本
  ReceiveVideo: 1 << 2, // 订阅此人视频
  NotSendVideo: 1 << 3, // 不给此人发视频
  Inviting: 1 << 4, // 正在邀请私聊
} as const

// ---------- 离开原因（对应老系统 ENUM_LEAVE）----------

export const LeaveReason = {
  Normal: 0,
  ServerShutdown: 1,
  Kicked: 2,
  NetworkError: 3,
  RelogonElsewhere: 4,
  RoomEnded: 5,
} as const

/** 离开原因的中文说明，用于界面提示。 */
export const LEAVE_REASON_TEXT: Record<number, string> = {
  0: '已退出房间',
  1: '服务器已关闭',
  2: '你已被请出房间',
  3: '网络连接已断开',
  4: '你的账号在别处登录',
  5: '主持人已结束本节课',
}

// ---------- 席位号与麦位号 ----------

/** 广播目标：发给房间全体。 */
export const USER_ALL = -1
/** 无效席位。 */
export const USER_NONE = -2
/** 未持麦。 */
export const MIC_NONE = -1

// ---------- 容量常量（对应老系统 stdconst.h）----------

export const Limits = {
  MaxMicNum: 8,
  MaxVideoNum: 16,
  MaxRoomUserNum: 2500,
  MaxTextLen: 30720,
  MaxWBObjNum: 100,
  MaxPageNum: 30,
  MaxRetainMsgNum: 200,
} as const

// ---------- 房间号编码 ----------

/** 由区域号、学校号、本地房间号合成全局房间号。对应老系统 r.jsp:local2Global。 */
export function globalRoomID(region: number, school: number, local: number): number {
  return ((region << 24) | (school << 16) | local) >>> 0
}

/** 拆解全局房间号。 */
export function splitRoomID(id: number): { region: number; school: number; local: number } {
  return {
    region: (id >>> 24) & 0xff,
    school: (id >>> 16) & 0xff,
    local: id & 0xffff,
  }
}

/** 一次分组最多几组，与服务端 MaxGroups 对应。 */
export const MaxGroups = 8
